/**
 * Brand-aware override for survey-kit's stock `account_create` renderer.
 *
 * Routes to the correct auth implementation based on the application's
 * `authProvider` config:
 *   - `"kinde"` (default): Kinde email-first + OTP flow
 *   - `"clerk"`: Headless Clerk custom flow (email OTP + optional SSO)
 */
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import {
  registerWithConnection,
  useBrandAuthConfig,
  type BrandConnectionKey,
} from "@nextday/brand-auth";
import { AccountCreate, AccountCreateDrawer } from "@nextday/funnel-ui";
import { accountCreateSchema, type AccountCreateData } from "@nextday/survey-kit";
import type { RendererProps, StepRenderer } from "@nextday/survey-kit";
import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";

import { saveAuthSession } from "@src/auth/AuthCallbackPage";
import { isSkipAuthEnabled } from "@src/auth/isSkipAuthEnabled";
import { useApplicationContext } from "@src/funnel/useApplicationContext";
import { ClerkEmailAuth } from "@src/funnel/clerkEmailAuth";

const useStableAdvance = (onAdvance: () => void) => {
  const ref = useRef(onAdvance);
  useEffect(() => {
    ref.current = onAdvance;
  });
  return useCallback(() => ref.current(), []);
};

const AuthSpinner = () => (
  <div className="flex flex-col items-center gap-3 p-6" role="status" aria-label="Resuming">
    <div className="size-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
  </div>
);

const KindeEmailFirstAuth = ({
  data,
  onAdvance,
  onAnswer,
}: {
  data: AccountCreateData;
  onAdvance: () => void;
  onAnswer: (answerKey: string, input: { value: unknown; action?: string }) => void;
}) => {
  const config = useBrandAuthConfig();
  const { register, isAuthenticated, isLoading } = useKindeAuth();
  const hasAdvancedRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const advanceOnce = useCallback(() => {
    if (hasAdvancedRef.current) return;
    hasAdvancedRef.current = true;
    onAdvance();
  }, [onAdvance]);

  useEffect(() => {
    if (isSkipAuthEnabled()) advanceOnce();
  }, [advanceOnce]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    advanceOnce();
  }, [isAuthenticated, isLoading, advanceOnce]);

  const handleEmailSubmit = useCallback(
    (email: string): void => {
      setSubmitting(true);
      setError(undefined);
      onAnswer(data.answerKey ?? "account_create", {
        value: email,
        action: "submit_email",
      });
      saveAuthSession({
        returnPath: window.location.pathname + window.location.search,
        authConfig: config,
      });
      void registerWithConnection({
        config,
        connection: "emailPasswordless",
        register,
        loginHint: email,
      }).catch(() => {
        setSubmitting(false);
        setError("Something went wrong. Please try again.");
      });
    },
    [config, register, onAnswer, data.answerKey],
  );

  const handleSocial = useCallback(
    (connection: BrandConnectionKey): void => {
      onAnswer(data.answerKey ?? "account_create", {
        value: connection,
        action: "sso_redirect",
      });
      saveAuthSession({
        returnPath: window.location.pathname + window.location.search,
        authConfig: config,
      });
      void registerWithConnection({ config, connection, register });
    },
    [config, register, onAnswer, data.answerKey],
  );

  if (isLoading || isAuthenticated) return <AuthSpinner />;

  const ssoProviders = data.ssoProviders ?? [];

  return (
    <AccountCreate
      question={data.question}
      description={data.description}
      eyebrow={data.eyebrow}
      portraitUrl={data.portraitUrl}
      prefilledEmail={data.prefilledEmail}
      emailPlaceholder={data.emailPlaceholder}
      ctaLabel={data.ctaLabel}
      termsUrl={data.termsUrl}
      privacyUrl={data.privacyUrl}
      isSubmitting={submitting}
      errorMessage={error}
      onSubmitEmail={handleEmailSubmit}
      onContinueWithGoogle={
        ssoProviders.includes("google") ? () => handleSocial("google") : undefined
      }
      onContinueWithApple={ssoProviders.includes("apple") ? () => handleSocial("apple") : undefined}
      ssoPosition={data.ssoPosition}
      separatorLabel={data.separatorLabel}
      inlineCta={data.inlineCta}
      disclaimer={data.disclaimer}
    />
  );
};

const AccountCreateOverrideComponent = ({
  data,
  step,
  onAdvance,
  onAnswer,
}: RendererProps<AccountCreateData>): ReactElement => {
  const stableAdvance = useStableAdvance(onAdvance);
  const ctx = useApplicationContext();
  const isClerk =
    ctx.status === "ready" &&
    ctx.application.authProvider === "clerk" &&
    !!ctx.application.clerkPublishableKey;

  const isDrawer = data.presentation === "drawer";

  const authContent = isClerk ? (
    <ClerkEmailAuth data={data} onAdvance={stableAdvance} onAnswer={onAnswer} />
  ) : (
    <KindeEmailFirstAuth data={data} onAdvance={stableAdvance} onAnswer={onAnswer} />
  );

  if (isDrawer) {
    return (
      <AccountCreateDrawer open testId={`drawer-account-create-${step.id}`}>
        {authContent}
      </AccountCreateDrawer>
    );
  }

  return authContent;
};

export const accountCreateOverride: StepRenderer<AccountCreateData> = {
  type: "account_create",
  dataSchema: accountCreateSchema,
  label: "Account create (auth wrapper)",
  Component: AccountCreateOverrideComponent,
};

export default accountCreateOverride;
